// The React side of returning to the clicked item; see return-focus.ts.
export { returnTarget } from './return-focus.ts';
import * as React from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { RETURN_FOCUS_CLASS, RETURN_FOCUS_MS, returnFocusSelector, shouldReturn, withReturnFocus } from './return-focus.ts';
import type { GameFacts, StoreStatus } from '@chess/api-model';
import { useStoreState } from '@chess/api-model/react';
import { edgeIndex, gameParam, selectedIndex, stepIndex, withGame } from './game-selection.ts';

/**
 * A function that opens `path` after marking `id` as the item to return to:
 * the current entry is replaced with one whose state names the item (without
 * moving the scroll), then `path` is pushed as usual.
 */
export function useOpenAndReturn(): (path: string, id: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return React.useCallback(
    (path: string, id: string) => {
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: withReturnFocus(location.state, id),
        preventScrollReset: true,
      });
      navigate(path);
    },
    [navigate, location],
  );
}

/**
 * Call in a list once: when the user comes back to this screen, and the list
 * is `ready` (its real items rendered, not the loading skeleton), the item
 * clicked before leaving is scrolled to the middle, focused and highlighted.
 * Does nothing if that item is gone.
 */
export function useReturnFocus(ready: boolean): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const handled = React.useRef<string>();
  React.useEffect(() => {
    const id = shouldReturn({ navigationType, state: location.state, ready, entryKey: location.key, handled: handled.current });
    if (!id) return;
    handled.current = location.key;
    // After <ScrollRestoration /> has put the window back, so this refines it.
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(returnFocusSelector(id));
      if (!el) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
      if (el.tabIndex < 0 && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      el.focus({ preventScroll: true });
      el.classList.add(RETURN_FOCUS_CLASS);
      window.setTimeout(() => el.classList.remove(RETURN_FOCUS_CLASS), RETURN_FOCUS_MS);
    });
    return () => cancelAnimationFrame(frame);
  }, [navigationType, location.key, location.state, ready]);
}

type GetApi = <T>(name: string) => Promise<T>;

/** The selected game and the period's games around it; see game-selection.ts. */
export interface SelectedGame {
  status: StoreStatus | 'loading';
  /** The reader's games of the period, newest first. */
  games: readonly GameFacts[];
  /** The selected game's index in `games`; -1 without games. */
  index: number;
  game?: GameFacts;
  /** Selects a game by id; replaces the address's entry, keeps the scroll. */
  select(id: string): void;
  /** Steps to the newer (-1) or older (+1) game. */
  step(delta: number): void;
  /** Selects the oldest (`first`) or the latest (`last`) game of the period. */
  jump(edge: 'first' | 'last'): void;
}

/**
 * The game the dashboard screens' per-game parts show: `?game=<id>` in the
 * address, the latest game of the period without it. Changing it replaces
 * the current history entry (no entry per step) and does not move the
 * scroll, so going back still leaves the screen.
 */
export function useSelectedGame(getApi: GetApi): SelectedGame {
  const { state } = useStoreState(getApi);
  const location = useLocation();
  const navigate = useNavigate();
  const games = React.useMemo(
    () => (state?.games ?? []).map((g) => g.facts).filter((f): f is GameFacts => !!f),
    [state?.games],
  );
  const ids = React.useMemo(() => games.map((g) => g.id), [games]);
  const index = selectedIndex(ids, gameParam(location.search));
  const select = React.useCallback(
    (id: string) =>
      navigate(withGame(`${location.pathname}${location.search}${location.hash}`, id), {
        replace: true,
        preventScrollReset: true,
        state: location.state,
      }),
    [navigate, location],
  );
  const step = React.useCallback(
    (delta: number) => {
      const next = stepIndex(ids.length, index, delta);
      if (next >= 0 && next !== index) select(ids[next]);
    },
    [ids, index, select],
  );
  const jump = React.useCallback(
    (edge: 'first' | 'last') => {
      const next = edgeIndex(ids.length, edge);
      if (next >= 0 && next !== index) select(ids[next]);
    },
    [ids, index, select],
  );
  return { status: state?.status ?? 'loading', games, index, game: games[index], select, step, jump };
}
